import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Permission } from '../../api/app-types/permission.app';
import { PERMISSION_API_PROVIDER } from '../../api/services/permission.api';
import { LOGGER } from '../logging/log.service';
import { LocalStorageService } from './local-storage.service';

export type SessionInvalidation =
	| { reason: 'recovery' }
	| { reason: 'logout'; localAccountId?: string }
	| { reason: 'auth-loss' };

export type SessionInvalidationListener = (event: SessionInvalidation) => void;

@Injectable()
export class SessionManagerService implements OnDestroy {
	private localStorageService = inject(LocalStorageService);
	private logger = inject(LOGGER);
	private readonly permissionApi = inject(PERMISSION_API_PROVIDER);
	private readonly SESSION_ID_KEY = 'app_current_session_id';
	private currentSessionId?: string;
	private internalPermissions = signal<Permission[]>([]);
	private permissionsLoaded = signal(false);
	private pendingPermissionLoad?: Promise<void>;
	private generationValue = 0;
	private invalidated = false;
	private readonly invalidationListeners = new Set<SessionInvalidationListener>();
	private readonly establishListeners = new Set<() => void>();

	public permissions = this.internalPermissions.asReadonly();

	public get generation(): number {
		return this.generationValue;
	}

	public constructor() {
		this.handleStorageChange = this.handleStorageChange.bind(this);
		window.addEventListener('storage', this.handleStorageChange);
	}

	public ngOnDestroy() {
		window.removeEventListener('storage', this.handleStorageChange);
	}

	/**
	 * Starts a new session for a user.
	 * @param userId The ID of the user starting the session.
	 * @returns The generated session ID.
	 */
	public startSession(userId: string) {
		this.currentSessionId = this.generateSessionId();
		this.localStorageService.setItem(this.SESSION_ID_KEY, this.currentSessionId);
		this.localStorageService.setCurrentUserId(userId);
		this.updateUserIndex(userId, this.currentSessionId);
		this.permissionsLoaded.set(false);
		this.pendingPermissionLoad = this.fetchUserPermissions();
		this.establish();
		return this.currentSessionId;
	}

	public async waitForPermissionsLoaded() {
		if (this.invalidated) return;
		if (this.permissionsLoaded()) return;
		// Guards can run before startSession() wires pendingPermissionLoad.
		// In that case, proactively start a single permission fetch here to avoid
		// redirecting to /error with a temporarily empty permission set.
		if (!this.pendingPermissionLoad) {
			this.pendingPermissionLoad = this.fetchUserPermissions();
		}
		await this.pendingPermissionLoad;
	}

	public async refreshPermissions() {
		if (this.invalidated) return;
		// Explicit refresh path for runtime role/permission updates (e.g. changed by admin)
		// without requiring the user to log out and back in.
		this.permissionsLoaded.set(false);
		this.pendingPermissionLoad = this.fetchUserPermissions();
		await this.pendingPermissionLoad;
	}

	/**
	 * Fetches the permissions of the current user via API and sets them inside this session service
	 * so that they can be used by the app itself internally.
	 */
	private async fetchUserPermissions() {
		const generation = this.generation;
		this.logger.log('🔑 Fetching user permissions for current session...');
		const maxRange = 500;
		try {
			// First permission call right after login can fail transiently while auth state settles.
			// Retry once before falling back to an empty permission set.
			const response = await this.getPermissionsWithRetry(maxRange, 1, generation);
			if (generation !== this.generation || this.invalidated) return;
			this.internalPermissions.set(response.entities);
			this.logger.log('🔑 Fetched user permissions: ', false, response);
		} catch (error) {
			if (generation !== this.generation || this.invalidated) return;
			this.internalPermissions.set([]);
			this.logger.warn('🔑 Failed to fetch user permissions. Continuing with empty permission set.', error);
		} finally {
			if (generation === this.generation && !this.invalidated) this.permissionsLoaded.set(true);
		}
	}

	private async getPermissionsWithRetry(
		maxRange: number,
		retries: number,
		generation: number,
	): Promise<Awaited<ReturnType<typeof this.permissionApi.getAll>>> {
		try {
			return await this.permissionApi.getAll(0, maxRange);
		} catch (error) {
			if (retries <= 0 || generation !== this.generation || this.invalidated) {
				throw error;
			}
			this.logger.warn('ðŸ”‘ Permission fetch failed. Retrying once...', error);
			return this.getPermissionsWithRetry(maxRange, retries - 1, generation);
		}
	}

	/**
	 * Ends the current session.
	 * @param userId The ID of the user ending the session.
	 */
	public endSession(userId?: string) {
		this.invalidate({ reason: 'logout', localAccountId: userId });
		const index = userId ? this.localStorageService.getUserDraftIndex(userId) : undefined;
		if (userId && index && index.sessionId === this.currentSessionId) {
			index.sessionId = '';
			this.localStorageService.setUserDraftIndex(userId, index);
		}
		this.localStorageService.removeItem(this.SESSION_ID_KEY);
		this.localStorageService.clearCurrentUserId();
		this.currentSessionId = undefined;
	}

	/** Invalidate before notifying owners so late responses cannot repopulate a closed session. */
	public invalidate(event: SessionInvalidation = { reason: 'recovery' }): void {
		this.generationValue++;
		this.invalidated = true;
		this.internalPermissions.set([]);
		this.permissionsLoaded.set(false);
		this.pendingPermissionLoad = undefined;
		for (const listener of [...this.invalidationListeners]) {
			try {
				listener(event);
			} catch (error) {
				this.logger.warn('Session invalidation listener failed.', error);
			}
		}
	}

	public onInvalidate(listener: SessionInvalidationListener): () => void {
		this.invalidationListeners.add(listener);
		return () => this.invalidationListeners.delete(listener);
	}

	public establish(): void {
		this.invalidated = false;
		for (const listener of [...this.establishListeners]) {
			try {
				listener();
			} catch (error) {
				this.logger.warn('Session establishment listener failed.', error);
			}
		}
	}

	public onEstablish(listener: () => void): () => void {
		this.establishListeners.add(listener);
		return () => this.establishListeners.delete(listener);
	}

	/**
	 * @returns The current session ID.
	 */
	public getSessionId() {
		if (!this.currentSessionId) {
			this.currentSessionId = this.localStorageService.getItem<string>(this.SESSION_ID_KEY);
		}
		return this.currentSessionId;
	}

	/**
	 * Validates the current session against the user's draft index.
	 * @param userId The ID of the user.
	 * @returns True if the session is valid, false otherwise.
	 */
	public isSessionValid(userId: string) {
		const index = this.localStorageService.getUserDraftIndex(userId);
		const sessionId = this.getSessionId();
		return !!(index && sessionId && index.sessionId === sessionId);
	}

	private generateSessionId() {
		return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
	}

	private updateUserIndex(userId: string, sessionId: string) {
		let index = this.localStorageService.getUserDraftIndex(userId);
		if (!index) {
			index = {
				userId,
				sessionId,
				draftKeys: [],
				lastActivity: Date.now(),
			};
		} else {
			index.sessionId = sessionId;
			index.lastActivity = Date.now();
		}
		this.localStorageService.setUserDraftIndex(userId, index);
	}

	private handleStorageChange(event: StorageEvent) {
		if (event.key !== this.SESSION_ID_KEY) return;
		this.currentSessionId = event.newValue ? event.newValue : undefined;
	}
}
